<template>
  <v-container class="py-6" max-width="1200">
    <!-- Header -->
    <div class="d-flex align-center mb-1">
      <v-btn icon variant="text" to="/admin" size="small" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon color="primary" size="28" class="mr-3">mdi-account-group</v-icon>
      <h1 class="text-h5 font-weight-bold">Brugere</h1>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-6 ml-12">Administrer alle brugere på platformen</p>

    <!-- Search & Filter Bar -->
    <v-card variant="outlined" class="mb-4">
      <v-card-text class="d-flex align-center ga-3 flex-wrap">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Søg brugere..."
          density="compact"
          variant="outlined"
          hide-details
          clearable
          class="flex-grow-1"
          style="max-width: 400px"
          @update:model-value="debouncedFetch"
        />
        <v-select
          v-model="filterRole"
          :items="roleOptions"
          label="Rolle"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 160px"
          @update:model-value="fetchUsers"
        />
        <v-select
          v-model="filterStatus"
          :items="statusOptions"
          label="Abonnement"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 160px"
          @update:model-value="fetchUsers"
        />
        <v-spacer />
        <v-chip variant="tonal" color="primary">
          {{ totalUsers }} brugere
        </v-chip>
      </v-card-text>
    </v-card>

    <!-- Users Table -->
    <v-card variant="outlined">
      <v-table v-if="users.length" density="comfortable" hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Bruger</th>
            <th>Email</th>
            <th>Oprettet</th>
            <th>Abonnement</th>
            <th>Rolle</th>
            <th class="text-right">Handlinger</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in users" :key="u.userId">
            <td class="text-medium-emphasis">#{{ u.userId }}</td>
            <td>
              <div class="d-flex align-center">
                <v-avatar color="primary" size="28" class="mr-2">
                  <span class="text-caption">{{ getInitials(u) }}</span>
                </v-avatar>
                <span class="font-weight-medium">{{ u.displayName || u.username }}</span>
              </div>
            </td>
            <td>{{ u.email }}</td>
            <td>{{ formatDate(u.joinDate) }}</td>
            <td>
              <v-chip
                size="small"
                :color="subscriptionColor(u.subscription?.status)"
                variant="tonal"
              >
                {{ subscriptionLabel(u.subscription?.status) }}
              </v-chip>
            </td>
            <td>
              <v-chip size="small" :color="roleColor(u)" variant="tonal">
                {{ roleLabel(u) }}
              </v-chip>
            </td>
            <td class="text-right">
              <v-btn icon size="small" variant="text" @click="openEditDialog(u)">
                <v-icon size="18">mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">Rediger</v-tooltip>
              </v-btn>
              <v-btn icon size="small" variant="text" color="error" @click="confirmDelete(u)">
                <v-icon size="18">mdi-delete</v-icon>
                <v-tooltip activator="parent" location="top">Slet</v-tooltip>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-card-text v-else-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" />
        <p class="text-body-2 text-medium-emphasis mt-3">Indlæser brugere...</p>
      </v-card-text>

      <v-card-text v-else class="text-center text-medium-emphasis py-12">
        <v-icon size="48" class="mb-3">mdi-account-search</v-icon>
        <p>Ingen brugere fundet. API-endepunkterne skal implementeres.</p>
      </v-card-text>
    </v-card>

    <!-- Edit User Dialog -->
    <v-dialog v-model="editDialog" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-account-edit</v-icon>
          Rediger bruger
        </v-card-title>
        <v-divider />
        <v-card-text v-if="editUser">
          <v-text-field
            v-model="editUser.displayName"
            label="Visningsnavn"
            variant="outlined"
            density="compact"
            class="mb-3"
          />
          <v-text-field
            v-model="editUser.email"
            label="Email"
            variant="outlined"
            density="compact"
            class="mb-3"
          />
          <v-text-field
            v-model="editUser.username"
            label="Brugernavn"
            variant="outlined"
            density="compact"
            class="mb-3"
          />

          <!-- Role section -->
          <div class="text-subtitle-2 mb-2">Rolle</div>
          <v-switch
            v-model="editUser.isAdmin"
            label="Administrator"
            color="primary"
            density="compact"
            hide-details
            class="mb-1"
          />
          <v-switch
            v-model="editUser.isModerator"
            label="Moderator (adgang til træning)"
            color="warning"
            density="compact"
            hide-details
            :disabled="editUser.isAdmin"
            class="mb-3"
          />
          <v-alert
            v-if="editUser.isModerator && !editUser.isAdmin"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Moderatorer kan tilgå træningsværktøjerne og labeling, men har ikke adgang til admin-panelet.
          </v-alert>

          <v-switch
            v-model="editUser.emailVerified"
            label="Email bekræftet"
            color="success"
            density="compact"
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="editDialog = false">Annuller</v-btn>
          <v-btn variant="flat" color="primary" :loading="saving" @click="saveUser">
            Gem ændringer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-error d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Slet bruger
        </v-card-title>
        <v-card-text>
          Er du sikker på, at du vil slette
          <strong>{{ deleteTarget?.displayName || deleteTarget?.username }}</strong>?
          Denne handling kan ikke fortrydes.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Annuller</v-btn>
          <v-btn variant="flat" color="error" :loading="deleting" @click="doDelete">
            Slet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { getUsers, updateUser, deleteUser } from "../../services/adminApi";

const users = ref([]);
const totalUsers = ref(0);
const loading = ref(false);
const search = ref("");
const filterRole = ref("all");
const filterStatus = ref("all");

const roleOptions = [
  { title: "Alle", value: "all" },
  { title: "Admin", value: "admin" },
  { title: "Moderator", value: "moderator" },
  { title: "Bruger", value: "user" },
];
const statusOptions = [
  { title: "Alle", value: "all" },
  { title: "Aktiv", value: "active" },
  { title: "Gratis", value: "free" },
];

// Edit
const editDialog = ref(false);
const editUser = ref(null);
const saving = ref(false);

// Delete
const deleteDialog = ref(false);
const deleteTarget = ref(null);
const deleting = ref(false);

let debounceTimer = null;
function debouncedFetch() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fetchUsers, 300);
}

async function fetchUsers() {
  loading.value = true;
  try {
    const params = {};
    if (search.value) params.search = search.value;
    if (filterRole.value !== "all") params.role = filterRole.value;
    if (filterStatus.value !== "all") params.subscription = filterStatus.value;

    const { data } = await getUsers(params);
    users.value = data.users || [];
    totalUsers.value = data.total ?? users.value.length;
  } catch {
    // API not implemented yet
  }
  loading.value = false;
}

// --- Role helpers ---
function roleColor(u) {
  if (u.isAdmin) return 'primary';
  if (u.isModerator) return 'warning';
  return 'grey';
}

function roleLabel(u) {
  if (u.isAdmin) return 'Admin';
  if (u.isModerator) return 'Moderator';
  return 'Bruger';
}

function getInitials(u) {
  const name = u.displayName || u.username || "?";
  return name.substring(0, 2).toUpperCase();
}

function formatDate(dateStr) {
  if (!dateStr) return "–";
  return new Date(dateStr).toLocaleDateString("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function subscriptionColor(status) {
  if (status === "active") return "success";
  if (status === "trialing") return "info";
  if (status === "past_due") return "warning";
  if (status === "canceled") return "error";
  return "grey";
}

function subscriptionLabel(status) {
  if (status === "active") return "Aktiv";
  if (status === "trialing") return "Prøve";
  if (status === "past_due") return "Forfalden";
  if (status === "canceled") return "Annulleret";
  return "Gratis";
}

function openEditDialog(u) {
  editUser.value = { ...u };
  editDialog.value = true;
}

// Auto-grant moderator when admin is toggled on
watch(() => editUser.value?.isAdmin, (isAdmin) => {
  if (isAdmin && editUser.value) {
    editUser.value.isModerator = true;
  }
});

async function saveUser() {
  saving.value = true;
  try {
    await updateUser(editUser.value.userId, {
      displayName: editUser.value.displayName,
      email: editUser.value.email,
      username: editUser.value.username,
      isAdmin: editUser.value.isAdmin,
      isModerator: editUser.value.isModerator,
      emailVerified: editUser.value.emailVerified,
    });
    editDialog.value = false;
    await fetchUsers();
  } catch (err) {
    alert("Fejl ved opdatering: " + (err.response?.data?.error || err.message));
  }
  saving.value = false;
}

function confirmDelete(u) {
  deleteTarget.value = u;
  deleteDialog.value = true;
}

async function doDelete() {
  deleting.value = true;
  try {
    await deleteUser(deleteTarget.value.userId);
    deleteDialog.value = false;
    await fetchUsers();
  } catch (err) {
    alert("Fejl ved sletning: " + (err.response?.data?.error || err.message));
  }
  deleting.value = false;
}

onMounted(fetchUsers);
</script>